import type { ChatMessage } from '@/types'

export const APP_MESSAGE_EVENT = 'wxproto-desk:message'
export const APP_MESSAGE_STREAM_ERROR = 'wxproto-desk:message-stream-error'

type MessageListener = (message: ChatMessage) => void

type StreamEntry = {
  source: EventSource
  listeners: Set<MessageListener>
  reconnectTimer: number | null
  reconnectAttempts: number
  stableTimer: number | null
}

// EventSource.CLOSED literal; test stubs may omit static constants.
const READY_STATE_CLOSED = 2

const RECONNECT_BASE_DELAY_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 30_000
// The server flushes 200 headers before it re-checks auth (it revalidates on a
// 15 s timer), so `open` only means "headers arrived" — a stream whose account
// binding was revoked still opens, then dies ~15 s later. Resetting the backoff
// on `open` would therefore pin such a stream at the 1 s delay forever, so the
// counter is only cleared once a connection has actually proven itself.
const CONNECTION_STABLE_MS = 30_000

const streams = new Map<string, StreamEntry>()

function clearStableTimer(entry: StreamEntry): void {
  if (entry.stableTimer === null) return
  window.clearTimeout(entry.stableTimer)
  entry.stableTimer = null
}

function markStable(entry: StreamEntry): void {
  clearStableTimer(entry)
  entry.reconnectAttempts = 0
}

function connect(wxid: string, entry: StreamEntry): void {
  const source = new EventSource('/api/bot/messages/events?wxid=' + encodeURIComponent(wxid))
  entry.source = source
  source.addEventListener('open', () => {
    clearStableTimer(entry)
    entry.stableTimer = window.setTimeout(() => markStable(entry), CONNECTION_STABLE_MS)
  })
  source.addEventListener('message', (event: MessageEvent) => {
    // A delivered message means the stream survived auth revalidation.
    markStable(entry)
    let payload: { message?: ChatMessage }
    try {
      payload = JSON.parse(event.data) as { message?: ChatMessage }
    } catch {
      return
    }
    if (!payload.message) return
    window.dispatchEvent(new CustomEvent(APP_MESSAGE_EVENT, { detail: payload.message }))
    for (const listener of entry.listeners) listener(payload.message)
  })
  source.addEventListener('error', () => {
    clearStableTimer(entry)
    window.dispatchEvent(new CustomEvent(APP_MESSAGE_STREAM_ERROR, { detail: { accountWxid: wxid } }))
    // Browser handles CONNECTING; CLOSED needs an explicit rebuild.
    if (source.readyState === READY_STATE_CLOSED) scheduleReconnect(wxid, entry)
  })
}

function scheduleReconnect(wxid: string, entry: StreamEntry): void {
  if (entry.reconnectTimer !== null) return
  const delay = Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_BASE_DELAY_MS * 2 ** Math.min(entry.reconnectAttempts, 10),
  )
  entry.reconnectAttempts += 1
  entry.reconnectTimer = window.setTimeout(() => {
    entry.reconnectTimer = null
    // The entry may have been unsubscribed (or replaced) while waiting.
    if (streams.get(wxid) !== entry || entry.listeners.size === 0) return
    entry.source.close()
    connect(wxid, entry)
  }, delay)
}

function closeEntry(entry: StreamEntry): void {
  if (entry.reconnectTimer !== null) {
    window.clearTimeout(entry.reconnectTimer)
    entry.reconnectTimer = null
  }
  clearStableTimer(entry)
  entry.source.close()
}

export function subscribeAccountMessages(wxid: string, listener: MessageListener): () => void {
  let entry = streams.get(wxid)
  if (!entry) {
    entry = {
      source: undefined as unknown as EventSource,
      listeners: new Set(),
      reconnectTimer: null,
      reconnectAttempts: 0,
      stableTimer: null,
    }
    connect(wxid, entry)
    streams.set(wxid, entry)
  } else if (entry.source.readyState === READY_STATE_CLOSED && entry.reconnectTimer === null) {
    // Rebuild cache hits whose source is permanently closed.
    connect(wxid, entry)
  }
  entry.listeners.add(listener)
  return () => {
    const current = streams.get(wxid)
    if (!current) return
    current.listeners.delete(listener)
    if (current.listeners.size > 0) return
    closeEntry(current)
    streams.delete(wxid)
  }
}

export function closeAllAccountMessageStreamsForTests(): void {
  for (const entry of streams.values()) {
    closeEntry(entry)
  }
  streams.clear()
}
